import { useState, useEffect, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api';
import { useAuth } from '../auth';
import { ROLE_LABELS } from '../permissions';
import { Button } from '../design';

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();

  const settingsQ = useQuery({ queryKey: ['ozon-settings'], queryFn: api.ozonSettings });

  const [clientId, setClientId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [premium, setPremium] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQ.data) {
      setClientId(settingsQ.data.clientId);
      setPremium(settingsQ.data.premium);
    }
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () => api.ozonSettingsSave({ clientId, apiKey: apiKey || undefined, premium }),
    onSuccess: () => {
      setSaved('Сохранено');
      setApiKey('');
      qc.invalidateQueries({ queryKey: ['ozon-settings'] });
      setTimeout(() => setSaved(null), 3000);
    },
  });

  const test = useMutation({ mutationFn: api.ozonSettingsTest });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <>
      <div className="page-header"><h2>Настройки</h2></div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Ozon Seller API</h3>
        {settingsQ.isLoading ? 'Загрузка…' : (
          <form onSubmit={onSubmit}>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Client-Id</div>
              <input
                type="text"
                required
                disabled={!isAdmin}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                style={{ width: 320, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Api-Key {settingsQ.data?.hasApiKey && <span className="muted">(сохранён: {settingsQ.data.apiKeyMasked} — оставь пустым чтобы не менять)</span>}
              </div>
              <input
                type="password"
                disabled={!isAdmin}
                placeholder={settingsQ.data?.hasApiKey ? '••••••••' : 'UUID из ЛК Ozon'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                style={{ width: 320, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 16 }}>
              <input
                type="checkbox"
                disabled={!isAdmin}
                checked={premium}
                onChange={(e) => setPremium(e.target.checked)}
              />{' '}
              У магазина включён Ozon Premium (доступны расширенные метрики)
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {isAdmin && (
                <>
                  <Button type="submit" variant="primary" loading={save.isPending}>
                    Сохранить
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => test.mutate()} loading={test.isPending} disabled={!settingsQ.data?.hasApiKey}>
                    Проверить подключение
                  </Button>
                </>
              )}
              {saved && <span style={{ color: 'var(--green)', fontSize: 13 }}>✓ {saved}</span>}
              {test.data && (
                <span style={{ fontSize: 13, color: test.data.ok ? 'var(--green)' : 'var(--red)' }}>
                  {test.data.ok ? `✓ Подключение работает (товаров: ${test.data.totalProducts})` : `✗ ${test.data.error}`}
                </span>
              )}
            </div>

            {settingsQ.data?.verifiedAt && (
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                Последняя успешная проверка: {new Date(settingsQ.data.verifiedAt).toLocaleString('ru-RU')}
              </div>
            )}
            {settingsQ.data?.lastError && (
              <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 12 }}>
                Последняя ошибка: {settingsQ.data.lastError}
              </div>
            )}
          </form>
        )}
      </div>

      {isAdmin && <BffAgentBlock />}
      {isAdmin && <TelegramBlock />}
      {isAdmin && <UsersBlock />}
    </>
  );
}

function TelegramBlock() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['tg-settings'], queryFn: api.tgSettings });
  const [enabled, setEnabled] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [monitoringChatId, setMonitoringChatId] = useState('');
  const [proxy, setProxy] = useState('');

  useEffect(() => {
    if (settingsQ.data) {
      setEnabled(settingsQ.data.enabled);
      setChatId(settingsQ.data.chatId);
      setMonitoringChatId(settingsQ.data.monitoringChatId);
      setProxy(settingsQ.data.proxy);
    }
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () => api.tgSettingsSave({
      enabled,
      botToken: botToken || undefined,
      chatId: chatId || null,
      monitoringChatId: monitoringChatId || null,
      proxy: proxy || null,
    }),
    onSuccess: () => {
      setBotToken('');
      qc.invalidateQueries({ queryKey: ['tg-settings'] });
    },
  });

  const test = useMutation({ mutationFn: api.tgSettingsTest });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Telegram-уведомления</h3>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Используется для алертов watchdog'а (зависание воркеров), AI-брифингов, daily-summary.
        Создай бота через @BotFather, получи токен и узнай свой chat ID через @userinfobot.
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span style={{ fontSize: 13, fontWeight: 500 }}>Включить отправку уведомлений в Telegram</span>
      </label>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          Bot Token {settingsQ.data?.hasBotToken && <span className="muted">(сохранён: {settingsQ.data.botTokenMasked} — оставь пустым чтобы не менять)</span>}
        </div>
        <input
          type="password"
          placeholder={settingsQ.data?.hasBotToken ? '••••••••' : '1234567890:ABCdef-GHIjklMNOpqrSTU'}
          value={botToken}
          onChange={(e) => setBotToken(e.target.value)}
          style={{ width: 400, padding: 8, border: '1px solid var(--color-border-strong)', borderRadius: 6 }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>Chat ID для бизнес-уведомлений (daily-summary, RRP)</div>
        <input
          type="text"
          placeholder="например 123456789 или -1001234567890"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          style={{ width: 400, padding: 8, border: '1px solid var(--color-border-strong)', borderRadius: 6 }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          Chat ID для мониторинга (опционально) — watchdog алерты, unhandled errors, Redis-проблемы.
          Если пусто — все уведомления идут в основной chat.
        </div>
        <input
          type="text"
          placeholder="-1001111111111"
          value={monitoringChatId}
          onChange={(e) => setMonitoringChatId(e.target.value)}
          style={{ width: 400, padding: 8, border: '1px solid var(--color-border-strong)', borderRadius: 6 }}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          Прокси (опционально). Поддерживается 2 формата:
          <br />
          • <strong>HTTP/SOCKS-прокси</strong>: <code>http://127.0.0.1:8888</code> или <code>socks5://...</code>
          <br />
          • <strong>Reverse-proxy API</strong> (зеркало api.telegram.org): <code>https://bot.koleso.app/tg-api</code>
        </div>
        <input
          type="text"
          placeholder="https://bot.koleso.app/tg-api"
          value={proxy}
          onChange={(e) => setProxy(e.target.value)}
          style={{ width: 400, padding: 8, border: '1px solid var(--color-border-strong)', borderRadius: 6 }}
        />
      </label>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>Сохранить</Button>
        <Button variant="secondary" onClick={() => test.mutate()} loading={test.isPending} disabled={!settingsQ.data?.hasBotToken || !chatId}>
          Отправить тест
        </Button>
        {save.isSuccess && !save.isPending && <span style={{ color: 'var(--color-success)', fontSize: 13 }}>✓ Сохранено</span>}
        {test.data && (
          <span style={{ fontSize: 13, color: test.data.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {test.data.ok ? '✓ Тест отправлен — проверь Telegram' : `✗ ${test.data.error}`}
          </span>
        )}
      </div>
    </div>
  );
}

function BffAgentBlock() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ['bff-status'], queryFn: api.bffStatus, refetchInterval: 15000 });
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: api.bffGenerateToken,
    onSuccess: (data) => {
      setRevealedToken(data.token);
      qc.invalidateQueries({ queryKey: ['bff-status'] });
    },
  });
  const revoke = useMutation({
    mutationFn: api.bffRevokeToken,
    onSuccess: () => {
      setRevealedToken(null);
      qc.invalidateQueries({ queryKey: ['bff-status'] });
    },
  });

  const s = statusQ.data;
  const apiBase = window.location.origin;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>BFF-агент (витринные цены Ozon)</h3>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Расширение Chrome на Windows-сервере собирает цены покупателей (включая «зелёную» цену с Ozon Картой) и присылает сюда.
        Эти цены недоступны через Seller API.
      </div>

      {s && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div className="stat">
            <div className="stat-label">Статус</div>
            <div className="stat-value" style={{ fontSize: 16 }}>
              {s.hasToken ? (
                <span className="badge badge-success">Токен выдан</span>
              ) : (
                <span className="badge">Не настроен</span>
              )}
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Последний синк</div>
            <div className="stat-value" style={{ fontSize: 16 }}>
              {s.lastPushAt ? new Date(s.lastPushAt).toLocaleString('ru-RU') : '—'}
            </div>
            {s.lastPushCount != null && <div className="stat-sub">{s.lastPushCount} товаров</div>}
          </div>
          <div className="stat">
            <div className="stat-label">Покрытие</div>
            <div className="stat-value" style={{ fontSize: 16 }}>
              {s.syncedProducts}/{s.totalProducts}
            </div>
            <div className="stat-sub">с витринной ценой</div>
          </div>
        </div>
      )}

      {s?.lastError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          <strong>Ошибка от агента:</strong> {s.lastError}
          {s.lastError.toLowerCase().includes('cookie') && (
            <div style={{ marginTop: 6 }}>→ Зайди на Windows-сервер, открой Chrome → seller.ozon.ru → залогинься заново (скан QR).</div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {!s?.hasToken ? (
          <Button variant="primary" onClick={() => generate.mutate()} loading={generate.isPending}>
            Сгенерировать токен
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => generate.mutate()} loading={generate.isPending}>
              Перегенерировать
            </Button>
            <Button
              variant="secondary"
              onClick={() => { if (confirm('Точно отозвать токен? Агент перестанет синкать.')) revoke.mutate(); }}
              loading={revoke.isPending}
            >
              Отозвать
            </Button>
          </>
        )}
      </div>

      {revealedToken && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Новый токен (сохрани сейчас, потом не покажем):</div>
          <code style={{ display: 'block', padding: 8, background: 'white', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>
            {revealedToken}
          </code>
          <Button
            variant="secondary"
            onClick={() => { navigator.clipboard.writeText(revealedToken); }}
            style={{ marginTop: 8 }}
          >
            Скопировать
          </Button>
        </div>
      )}

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 500 }}>Инструкция по установке расширения</summary>
        <ol style={{ marginTop: 12, lineHeight: 1.7, fontSize: 13 }}>
          <li>Скачай <a href="/extension.zip" download>extension.zip</a> и распакуй на Windows-сервер</li>
          <li>На Windows-сервере в Chrome открой <code>chrome://extensions/</code></li>
          <li>Включи переключатель <strong>«Режим разработчика»</strong> (вверху справа)</li>
          <li>Нажми <strong>«Загрузить распакованное расширение»</strong> → выбери распакованную папку</li>
          <li>Кликни на иконку расширения (паззл в шапке → закрепи Ozon Repricer Agent)</li>
          <li>В попапе введи:
            <ul>
              <li><strong>API URL:</strong> <code>{apiBase}</code></li>
              <li><strong>Token:</strong> токен из шага выше</li>
            </ul>
            и нажми «Подключить»
          </li>
          <li>Открой <a href="https://seller.ozon.ru" target="_blank" rel="noreferrer">seller.ozon.ru</a> → залогинься (скан QR через мобильное приложение Ozon)</li>
          <li>Готово. Раз в 30 минут расширение само будет тянуть цены</li>
        </ol>
      </details>
    </div>
  );
}

function UsersBlock() {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['users'], queryFn: api.users });
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'viewer' as 'admin' | 'manager' | 'warehouse' | 'viewer' });

  const create = useMutation({
    mutationFn: () => api.userCreate(newUser),
    onSuccess: () => {
      setCreating(false);
      setNewUser({ email: '', password: '', name: '', role: 'viewer' });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const del = useMutation({
    mutationFn: api.userDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => api.userUpdate(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Пользователи</h3>
        <Button variant="secondary" onClick={() => setCreating((v) => !v)}>{creating ? 'Отмена' : '+ Добавить'}</Button>
      </div>

      {creating && (
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 8, marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}
        >
          <input required placeholder="email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <input required placeholder="пароль (мин 8)" type="password" minLength={8} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <input placeholder="имя (опц)" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as typeof newUser.role })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}>
            <option value="admin">Администратор</option>
            <option value="manager">Менеджер маркетплейсов</option>
            <option value="warehouse">Сотрудник склада</option>
            <option value="viewer">Просмотр</option>
          </select>
          <Button type="submit" variant="primary" loading={create.isPending}>Создать</Button>
        </form>
      )}

      <table className="products">
        <thead>
          <tr><th>Email</th><th>Имя</th><th>Роль</th><th>Активен</th><th>Создан</th><th>Последний вход</th><th></th></tr>
        </thead>
        <tbody>
          {usersQ.data?.items.map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.name ?? '—'}</td>
              <td><span className="badge">{ROLE_LABELS[u.role as keyof typeof ROLE_LABELS] ?? u.role}</span></td>
              <td>
                <input type="checkbox" checked={u.active} onChange={(e) => toggleActive.mutate({ id: u.id, active: e.target.checked })} />
              </td>
              <td className="muted">{new Date(u.createdAt).toLocaleDateString('ru-RU')}</td>
              <td className="muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ru-RU') : '—'}</td>
              <td>
                <Button variant="secondary" onClick={() => { if (confirm(`Удалить ${u.email}?`)) del.mutate(u.id); }}>Удалить</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
